/**
 * This module is the back bone of community interactive site plans in the Search Results Page's map.
 * Interactions like showing/hiding markers on top of interactive community site plans are supported here.
 */

import { store, store$ } from '@redux/store';
import {
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    from,
    fromEvent,
    map,
    merge,
    mergeMap,
    pairwise,
    shareReplay,
    skip,
    startWith,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs';
import LoadingStatus from 'enums/loadingStatus';
import { 
    createInteractiveSitePlanMarkersOverlay,
    setZoomLevel,
    removeInteractiveMarkersOverlay,
    updateMapCenter,
    changeMapCursorPointer,
} from 'libraries/mapLibrary';
import { 
    ProductType,
    ResultType,
    searchParametersActions,
    selectAppliedFacets,
    selectProductType,
    selectResultType,
    selectSorting,
    SortType,
} from 'features/searchResultsFacets';
import { selectLayoutBreakpoint, selectModal } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import { onDocumentClicked$ } from 'layout/redux/observables';
import {
    selectAllPlansAndSpecsIds,
    selectSitePlans,
    selectFocusedListingAndSitePlans,
    selectInteractiveSitePlans,
    selectZoomLevel,
    selectLotCommunityId,
    selectAvailableSpec,
    selectedLotSummaryCardStatus,
    selectLotId,
    selectTrackedInteractiveSitePlans,
    selectLotInformation
} from '../redux/selectors';
import {
    addInteractiveSitePlan,
    assignLotPathId,
    drawInteractiveSitePlan,
    getCommunityInteractiveSitePlanInformation$,
    handleInteractiveMapLayersVisibility,
    removeInteractiveSitePlan
} from '../services/interactiveSitePlan';
import { interactiveSitePlanSlice, mapStateSlice }  from '../redux/slices';
import { onZoomChanged$ } from '../redux/observables';
import { zoomLevelTriggers } from '../constants/sitePlansConstants';
import LotPolygonType from '../enums/lotPolygonType';
import { getActiveMarker, getInteractiveSitePlanLayers } from '../services/sitePlan';
import LotStatus from '../enums/lotStatus';

const actions = {
    ...interactiveSitePlanSlice.actions,
    ...mapStateSlice.actions,
    ...searchParametersActions,
};

/**
 * Basic configuration for a functional experience of Community Interactive Site Plans.
 * This contains properties as the zoom triggers for fetching and drawing interactive
 * markers to place on top of the site plans.
 */
const configuration = {
    previousProductType: null,
    previousResultType: null,
    markersBuffer: new Set(),
    sitePlansBuffer: new Set(),
    searchWithinMapButton: document.querySelector('[data-search-with-map-trigger]'),
};

/**
 * getHiddenInteractiveSitePlans: this function filters interactive site plans to include only those that:
 * - Have their markers hidden (`!sitePlan.areMarkersShown`).
 * - Exist in the list of site plans (`selectSitePlans(state)`).
 *
 * @param {Object} state - The application state.
 * @returns {Array} Filtered interactive site plans.
 */
const getHiddenInteractiveSitePlans = (state) => {
    const sitePlans = new Set(selectSitePlans(state).map((sitePlan) => sitePlan.sitePlanId));

    return selectInteractiveSitePlans(state).filter(
        (sitePlan) => !sitePlan.areMarkersShown && sitePlans.has(sitePlan.sitePlanId)
    );
}

/**
 * addNonInteractiveStateClass: adds the "non-interactive-state" to the inactive groups
 * when the user exited the interactiveness threshold.
 * Important to notice: out of the interactiveness threshold; all the lots look like if they
 * were active. When the user enters into the interactiveness threshold: now the inactive lots
 * will look disable so the users won't click on them.
 */
const addNonInteractiveStateClass = () => {
    const inactiveGroups = document.querySelectorAll(`#lot_polygons #${LotPolygonType.Inactive.toString()}`);
    inactiveGroups.forEach((group) => group.classList.add("non-interactive-state"));
};

/**
 * dispatchEnterInteractiveSitePlanEvent: Dispatches a custom 'interactiveMapClick' event on the document body
 * when a user enters the interactive state for a site plan. The event includes the active community map details
 * in its detail property, which can be used by downstream listeners for analytics or UI updates.
 *
 * @param {Object} interactiveSitePlan - The interactive site plan object containing the communityId.
 */
const dispatchEnterInteractiveSitePlanEvent = (interactiveSitePlan) => {
    const activeMarker = getActiveMarker(interactiveSitePlan.communityId);
    const communityMap = {
        id: activeMarker.communityId,
        name: activeMarker.title,
        builderId: activeMarker.builderId,
        brandName: activeMarker.brandName,
        isInteractive: true,
    }

    document.body.dispatchEvent(
        new CustomEvent('interactiveMapClick', {
            detail: {
                communityMap
            },
        })
    );
}

/**
 * createInteractiveMarkers: will create interactive markers on top of an interactive site plan.
 */
const createInteractiveMarkers = (sitePlan, zoomLevel) => {
    const payload = {
        markers: sitePlan.markers,
        areMarkersShown: zoomLevel >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger,
        communityId: sitePlan.communityId,
        sitePlanId: sitePlan.sitePlanId,
    };

    store.dispatch(actions.setInteractiveSitePlan(payload));

    const markersContainer = document.querySelector(`[data-interactive-markers-container-id="${sitePlan.sitePlanId}"]`);
    if (markersContainer) {
        return;
    }

    const newMarkersContainer = document.createElement('DIV');
    newMarkersContainer.dataset.interactiveMarkersContainerId = sitePlan.sitePlanId;

    if (zoomLevel < zoomLevelTriggers.drawInteractiveSitePlanMarkers) {
        newMarkersContainer.setAttribute('hidden', '');
    }

    const resource = {
        communityId: sitePlan.communityId,
        sitePlanId: sitePlan.sitePlanId,
        onAdd: addInteractiveSitePlan,
        onDraw: drawInteractiveSitePlan,
        onRemove: removeInteractiveSitePlan,
        markers: structuredClone(sitePlan.markers),
        container: newMarkersContainer,
    };

    createInteractiveSitePlanMarkersOverlay(resource);
};

/**
 * getLotAvailableListings will filter the listings of a given lot based on the current user
 * search criteria.
 * @param {Array} lotListingIds is an array of the listings represented by the lot.
 * @param {Array} listings is an array of the current listings in the store (plan or spec ids).
 * @returns an array of the available listing ids in the given lot.
 */
const getLotAvailableListings = (lotListingIds, listings) =>
    lotListingIds.filter((listingId) => listings.includes(listingId));

/**
 * handleRemovedInteractiveSitePlans: this function will remove interactive site plans associated
 * with removed interactive communities.
 * @param {Array} removedInteractiveSitePlans is an array of interactive site plans.
 */
const handleRemovedInteractiveSitePlans = (removedInteractiveSitePlans) => {
    const sitePlanIds = new Set([...configuration.markersBuffer, ...configuration.sitePlansBuffer]);
    store.dispatch(actions.untrackInteractiveSitePlans());

    sitePlanIds.forEach((id) => {
      const sitePlan = removedInteractiveSitePlans.find((plan) => plan.sitePlanId === id);
      if (sitePlan) {
        configuration.markersBuffer.delete(id);
        configuration.sitePlansBuffer.delete(id);
        removeInteractiveMarkersOverlay(sitePlan.communityId);
        store.dispatch(actions.removeInteractiveSitePlan(id));
      }
    });
}

/**
 * setInteractiveSitePlanMarkersShownFlag function will change the "areMarkersShown" property of
 * a given Interactive Site Plan received as argument.
 * @param {Object} interactiveSitePlan to change the "areMarkersShown" property 
 * @param {Boolean} areMarkersShown boolean flag to set to the Interactive Site Plan
 */
const setInteractiveSitePlanMarkersShownFlag = (interactiveSitePlan, areMarkersShown) => {
    const interactiveSitePlanInStore = selectInteractiveSitePlans(store.getState());
    if (!interactiveSitePlanInStore.find((plan) => plan.sitePlanId === interactiveSitePlan.sitePlanId)) {
        return;
    }

    const payload = {
        ...interactiveSitePlan,
        areMarkersShown,
    };

    store.dispatch(actions.setInteractiveSitePlan(payload));
}
  
/** disableSearchWithinMap: will disable search within map flags in the store, and will turn off
* the search within map checkbox in the user interface.
*/
const disableSearchWithinMap = () => {
    store.dispatch(actions.disableSearchWithinMap());
    configuration.searchWithinMapButton.hidden = false;
};

/**
 * dispatchEnterInteractiveSitePlanResults function will dispatch an action to the
 * search parameters slice with the payload associated with entering into interactive
 * state of ISP.
 */
const dispatchEnterInteractiveSitePlanResults = () => {
    disableSearchWithinMap();

    const currentState = store.getState();
    const currentProduct = selectProductType(currentState);

    const payload = {};

    if (currentProduct !== ProductType.Homes.toString()) {
        configuration.previousProductType = selectProductType(currentState);
        configuration.previousResultType = selectResultType(currentState);
        
        payload.productType = ProductType.Homes.toString();
        payload.resultType = ResultType.Homes.toString();
    }

    if (Object.keys(payload).length > 0) {
        store.dispatch(actions.fetchInteractiveSitePlanResults(payload));
    }
}

/**
 * dispatchInteractiveSitePlanSorting: will dispatch the action for sort results based on
 * community identifiers.
 * @param {int} communityId is a community identifier.
 */
const dispatchInteractiveSitePlanSorting = (communityIds) => {
    const currentState = store.getState();
    const currentSort = selectSorting(currentState);
    const payload = {};

    if (!currentSort?.sortBy || currentSort.sortBy === SortType.CommunityId.toString()) {
        payload.sortBy = SortType.CommunityId.toString();
        payload.sortOrder = false;
        payload.sortFirstBy = communityIds.toString();
    }
    
    if (Object.keys(payload).length > 0) {
        store.dispatch(actions.fetchSort(payload));
    }
}

/**
 * dispatchExitInteractiveSitePlanResults: will dispatch the action for exiting interactive site
 * plans results. This action changes state values like the produc and result type.
 */
const dispatchExitInteractiveSitePlanResults = () => {
    const currentState = store.getState();
    const currentSort = selectSorting(currentState);
    const payload = {};
    
    if (currentSort?.sortBy === SortType.CommunityId.toString()) {
        payload.sorting = {
            sortBy: null,
            sortOrder: false,
            sortFirstBy: null,
        };
    }
    
    const productType = selectProductType(currentState);
    const resultType = selectResultType(currentState);
    const sitePlans = selectSitePlans(currentState)?.filter((sitePlan) => sitePlan.isInteractiveSitePlan);
    const isHomeResults = productType === ProductType.Homes.toString();

    payload.productType = (!isHomeResults || (isHomeResults && sitePlans.length === 0))
        ? productType
        : configuration.previousProductType || productType;
    
    payload.resultType = (!isHomeResults || (isHomeResults && sitePlans.length === 0)) 
        ? resultType
        : configuration.previousResultType || resultType;

    store.dispatch(actions.fetchInteractiveSitePlanResults(payload));
    if (payload.sorting) {
        store.dispatch(actions.fetchSort(payload));
    }

    configuration.previousProductType = null;
    configuration.previousResultType = null;
}

/**
 * removeNonInteractiveStateClass: removes the "non-interactive-state" to the inactive groups
 * when the user exits the interactiveness threshold.
 * Important to notice: out of the interactiveness threshold; all the lots look like if they
 * were active. When the user enters into the interactiveness threshold: now the disable lots
 * will look disable so the users won't click on them.
 */
const removeNonInteractiveStateClass = () => {
    const inactiveGroups = document.querySelectorAll(`#lot_polygons #${LotPolygonType.Inactive.toString()}`);
    inactiveGroups.forEach((group) => group.classList.remove("non-interactive-state"));

    const selectedGroups = document.querySelectorAll(`#lot_polygons #${LotPolygonType.Selected.toString()}`);
    selectedGroups.forEach((group) => { group.innerHTML = "" });
}

/**
 * handleMarkersVisibility will toggle the Interactive Site Plans markers visibility
 */
const handleMarkersVisibility = ({ interactiveSitePlans, zoomLevel }) => {
    const visibilityFlag = zoomLevel >= zoomLevelTriggers.drawInteractiveSitePlanMarkers;

    interactiveSitePlans.forEach((sitePlan) => {
        const container = document.querySelector(`[data-interactive-markers-container-id="${sitePlan.sitePlanId}"]`);
        container?.toggleAttribute('hidden', !visibilityFlag);
    });
 }

/**
 * removeInteractiveMarkers: will remove the interactive markers of a given array of community site plans.
 * @param {Array} interactiveSitePlans to remove. 
 */
const removeInteractiveMarkers = (interactiveSitePlans) => {
    interactiveSitePlans.forEach((interactiveSitePlan) => {
        setInteractiveSitePlanMarkersShownFlag(interactiveSitePlan, false);
        removeInteractiveMarkersOverlay(interactiveSitePlan.communityId);
    });
}

/**
 * getInteractiveSitePlans function will return the interactive site plans for a community id.
 * @param {int} id is the identifier of the community.
 * @param {Array} interactiveSitePlans collection to query
 * @returns a set of interactive site plans for a community.
 */
const getInteractiveSitePlans = (id, interactiveSitePlans) =>
    interactiveSitePlans?.filter((sitePlan) => sitePlan.communityId === id)

/**
 * updateLotPolygon function updates the look and feel of a list of polygons.
 */
const updateLotPolygon = (sitePlanId, groupName, polygons) => {
    const sitePlan = document.querySelector(`[data-polygon-container-id='${sitePlanId}']`);
    const group = sitePlan?.querySelector(`g#${groupName}`);
    group?.append(...polygons);
}

/**
 * removeHighlightedElements function will remove any polygon in the Hovered group of the shown site plans.
 */
const removeHighlightedElements = () => {
    const hoveredGroups = document.querySelectorAll(`#lot_polygons #${LotPolygonType.Hovered.toString()}`);
    hoveredGroups.forEach((group) => { group.innerHTML = '' });
}

/**
 * handleCursorPointerOnHoveringLots will adjust the map cursor depending on if there is an
 * interested lot from the user.
 */
const handleCursorPointerOnHoveringLots = ({ polygonId, polygonGroupId }) => {
    const cursor = polygonId && polygonGroupId === LotPolygonType.Active.toString() ? 'pointer' : 'grab';
    changeMapCursorPointer(cursor);
};

/**
 * clearSitePlanGroup removes polygons from a given site plan group.
 */
const clearSitePlanGroup = (sitePlanId, group) => {
    const sitePlan = document.querySelector(`[data-polygon-container-id="${sitePlanId}"]`);
    const selectedGroup = sitePlan.querySelector(`#lot_polygons #${group}`);
    selectedGroup.innerHTML = "";
}

/**
 * disableSitePlanInteraction function will update the polygons of the
 * community site plans shown on screen. After this update, the polygons
 * will no longer be interactive. The user will have to zoom in again
 * in order to make them interactive.
 */
const disableSitePlanInteraction = (interactiveSitePlans) => {
    removeInteractiveMarkers(interactiveSitePlans);
    dispatchExitInteractiveSitePlanResults();
    changeMapCursorPointer();
    removeHighlightedElements();
    store.dispatch(actions.removeLotInformation());

    interactiveSitePlans.forEach((interactiveSitePlan) => {
        configuration.markersBuffer.delete(interactiveSitePlan.sitePlanId);
        const sitePlanMap = document.querySelector(`[data-polygon-container-id="${interactiveSitePlan.sitePlanId}"]`);
        const lotPolygons = interactiveSitePlan.markers.map((marker) =>
            sitePlanMap?.querySelector(`#${LotPolygonType.Active.toString()} #${marker.Id}, #${LotPolygonType.Selected.toString()} #${marker.Id}`))
            .filter(Boolean);

        if (sitePlanMap) {
            const hoverGroup = sitePlanMap.querySelector(`#${LotPolygonType.Hovered.toString()}`);
            const selectedGroup = sitePlanMap.querySelector(`#${LotPolygonType.Selected.toString()}`);
            const activeGroup = sitePlanMap.querySelector(`#${LotPolygonType.Active.toString()}`);
            
            hoverGroup.innerHTML = "";
            selectedGroup.innerHTML = "";
            activeGroup.innerHTML = "";

            updateLotPolygon(interactiveSitePlan.sitePlanId, LotPolygonType.Inactive.toString(), lotPolygons);
        }
    });
 };

 /**
  * updateActiveLotPolygonStyle will handle previously active and current active
  * lot styles after clicking on a lot area.
  * @param {Object} previousActiveLot is the lot transtioning from selected to active.
  * @param {Object} currentActiveLot is the lot transitioning from active to selected.
  */
 const updateActiveLotPolygonStyle = (previousActiveLot, currentActiveLot) => {
    if (previousActiveLot.lotMarker?.Id && previousActiveLot?.sitePlanId) {
        const previousSitePlan = document.querySelector(`[data-polygon-container-id="${previousActiveLot.sitePlanId}"]`);
        const selectedGroup = previousSitePlan?.querySelector(`#${LotPolygonType.Selected.toString()}`);
        if (selectedGroup) {
            selectedGroup.innerHTML = "";
        }
    }

    if (currentActiveLot.lotMarker?.Id && currentActiveLot?.sitePlanId) {
        const currentSitePlan = document.querySelector(`[data-polygon-container-id="${currentActiveLot.sitePlanId}"]`);
        const lotPolygon = currentSitePlan?.querySelector(`path#${currentActiveLot.lotMarker?.Id}`);
        if (lotPolygon) {
            updateLotPolygon(
                currentActiveLot.sitePlanId,
                LotPolygonType.Selected.toString(),
                [lotPolygon.cloneNode(true)]);
        }
    }
}

/**
 * getLotById: finds and returns the path node representing a lot by a given identifier.
 * @param {string} lotId is the lot identifier.
 * @param {int} sitePlanId is the site map identifier.
 * @returns a path representing a community lot.
 */
const getLotById = (lotId, sitePlanId) => {
    const sitePlanSvg = document.querySelector(`[data-polygon-container-id="${sitePlanId}"]`);
    return sitePlanSvg?.querySelector(`g#lot_polygons path#${lotId}`);
}

/**
 * isInteractiveSitePlanShown validates that both the marker and site plan buffers contain
 * a given site plan id. To have these two buffers containing the given Id means that both
 * layers are being shown in the Search Results Map and the user is able to interact with
 * them.
 * @param {int} sitePlanId is the site plan identifier
 * @returns a boolean flag indicating if the interactive site plan is ready to be used.
 */
const isInteractiveSitePlanShown = (sitePlanId) =>
    configuration.markersBuffer.has(sitePlanId) &&
    configuration.sitePlansBuffer.has(sitePlanId);

/**
 * addPolygonToGroup: will add a lot polygon to a given array if the lot is not already
 * in the <g> node where the array of lots its going to be appended to. This is to avoid
 * unnecesary DOM operation by appending children to a <g> that already have them.
 * @param {HTMLElement} lotPolygon is a path representing a lot.
 * @param {String} groupId is a type of LotPolygonType.
 * @param {Array} array is the array to push the lot polygon.
 */
const addPolygonToGroup = (lotPolygon, groupId, array) => {
    if (!lotPolygon.closest(`g#${groupId}`)) {
        array.push(lotPolygon);
    }
};

/**
 * areFiltersApplied will return a boolean flag indicating if there are filters applied or not.
 * @param {Array} filters is an array of the current applied filters.
 * @returns a flag indicating if there are filters applied or not.
 */
const areFiltersApplied = (filters) => {
    const excludedFilters = ["sort"];
    const appliedFilters = filters?.filter((appliedFilter) => !excludedFilters.includes(appliedFilter));
    return appliedFilters?.length > 0;
};

/**
 * applyLotClassBasedOnStatus will apply a specific class to the lot based on its status.
 * @param {LotStatus} status is the lot status
 * @param {HTMLElement} lotPolygon is the <path> node representing a polygon
 */
const applyLotClassBasedOnStatus = (status, lotPolygon) => {
    if (status === LotStatus.Sold.toString()) {
        lotPolygon.classList.add('sold');
    }

    if (status === LotStatus.Unreleased.toString()) {
        lotPolygon.classList.add('unreleased');
    }     
}

/**
 * updateLotVisibility is in charge of showing or hidding lot visuals like the lot marker or the lot polygon.
 * @param {Array} listings is an array of either plan Ids or spec Ids, depending of the calling control. 
 * @param {Function} getMarkers is a function that will filter the Interactive Site Plan markers to retrieve either
 * plan or specs. Which one to choose depends on the calling control of updateLotVisibility function.
 * @param {Function} getAvailability is a function that will filter lot listings depending on their availability
 * in terms of the current user search criteria.
 */
const updateLotVisibility = ({ listings, getMarkers, getAvailability }) => {
    const currentState = store.getState();
    const zoomLevel = selectZoomLevel(currentState);
    const productType = selectProductType(currentState);

    if (productType !== ProductType.Homes.toString() || 
        zoomLevel < zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger) {
        return;
    }

    const selectedLotId = selectLotId(currentState);
    const activeFilters = selectAppliedFacets(currentState);
    const areFiltersActive = areFiltersApplied(activeFilters);

    const interactiveSitePlans =
        selectInteractiveSitePlans(currentState)
        .filter((sitePlan) => isInteractiveSitePlanShown(sitePlan.sitePlanId));

    interactiveSitePlans.forEach((interactiveSitePlan) => {
        const filteredMarkers =  interactiveSitePlan.markers?.filter(getMarkers);
        const activeLots = [];
        const inactiveLots = [];

        filteredMarkers?.forEach((marker) => {
            const lotPolygon = getLotById(marker.Id, interactiveSitePlan.sitePlanId);
            if (!lotPolygon) {
                return;
            }

            applyLotClassBasedOnStatus(marker.Status, lotPolygon); 
            const lotIsVisible =
                (marker.Status === LotStatus.Sold.toString() && !areFiltersActive) ||
                (marker.Status !== LotStatus.Sold.toString() && getAvailability(marker, listings).length > 0);

            const lotMarker = document.querySelector(`[data-interactive-marker-id="${marker.Id}"]`);
            lotMarker?.toggleAttribute('hidden', !lotIsVisible);      

            const isLotSelected = selectedLotId === marker.Id;

            if (!lotIsVisible) {
                addPolygonToGroup(lotPolygon, LotPolygonType.Inactive.toString(), inactiveLots);
                if (isLotSelected) {
                    store.dispatch(actions.removeLotInformation());
                }
            } else if (!isLotSelected) {
                addPolygonToGroup(lotPolygon, LotPolygonType.Active.toString(), activeLots);
            }
        });

        updateLotPolygon(interactiveSitePlan.sitePlanId, LotPolygonType.Active.toString(), activeLots);
        updateLotPolygon(interactiveSitePlan.sitePlanId, LotPolygonType.Inactive.toString(), inactiveLots);
    });
};

/**
 * updatePlansVisibility: This function calls the generic updateLotVisibility with plan-specific
 * filter and availability logic.
 * @param {Array} plans array of the current plans shown in the Search Results Page.
 */
const updatePlansVisibility = ({ plans }) => {
    updateLotVisibility({
        listings: plans,
        getMarkers: (marker) => marker.HousingUnitId === null,
        getAvailability: (marker, currentPlans) => getLotAvailableListings(marker.HousePlanIds, currentPlans),
    });
};

/**
 * updateSpecsVisibility: This function calls the generic updateLotVisibility with spec-specific
 * filter and availability logic.
 * @param {Array} specs array of the current specs shown in the Search Results Page.
 */
const updateSpecsVisibility = ({ specs }) => {
    updateLotVisibility({
        listings: specs,
        getMarkers: (marker) => marker.HousingUnitId,
        getAvailability: (marker, currentSpecs) => getLotAvailableListings([marker.HousingUnitId], currentSpecs),
    });
};

/**
 * dispatchEnterInteractiveSitePlanResults: will dispatch the action for entering interactive site
 * plans results. This action changes state values like produc and result type.
 */
const enableSitePlanInteraction = () => {
    const currentState = store.getState();
    const zoomLevel = selectZoomLevel(currentState);
    if (zoomLevel < zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger) {
        return;
    }

    dispatchEnterInteractiveSitePlanResults();

    const communityIdsForSorting = [];
    const interactiveSitePlans = selectInteractiveSitePlans(currentState);

    interactiveSitePlans
        .filter((sitePlan) => !sitePlan.areMarkersShown)
        .forEach((interactiveSitePlan) => {
            createInteractiveMarkers(interactiveSitePlan, zoomLevel);
            dispatchEnterInteractiveSitePlanEvent(interactiveSitePlan);
            communityIdsForSorting.push(interactiveSitePlan.communityId);
    });

    if (communityIdsForSorting.length > 0) {
        dispatchInteractiveSitePlanSorting(communityIdsForSorting);
    }

    const currentListingIds = selectAllPlansAndSpecsIds(currentState);
    updatePlansVisibility(currentListingIds);
    updateSpecsVisibility(currentListingIds);

    removeNonInteractiveStateClass();
}

/**
 * trackLoadedSitePlans: will dispatch the updateInteractiveSitePlanLoadingStatus reducer.
 * @param {Array} sitePlans is an array of the site plans to update.
 * @param {LoadingStatus} loadingStatus to set in the site plan.
 */
const trackLoadedSitePlans = (communityIds, loadingStatus) => {
    if (communityIds.length > 0) {
        communityIds.forEach((communityId) => {
            const payload = {
                communityId,
                loadingStatus,
            };
        
            store.dispatch(actions.trackLoadedSitePlans(payload));
        });
    }
}

/**
 * highlightLotArea: this function will highlight a given set of lots in a community site plan.
 * @param {int} id of the hovered card. It could be a plan or a spec id.
 * @param {Array} interactiveSitePlans array of Interactive Site Plans
 */
const highlightLotArea = ({ id, interactiveSitePlans }) => { 
    if (!id || interactiveSitePlans.length === 0) {
        return;
    }

    const activeLotId = selectLotId(store.getState());
    interactiveSitePlans.forEach((sitePlan) => {
        const sitePlanMap = document.querySelector(`[data-polygon-container-id="${sitePlan.sitePlanId}"]`);
        const polygonLots = sitePlan.markers
            .filter((marker) =>
                marker.Status !== LotStatus.Sold.toString() &&
                marker.Id !== activeLotId &&
                (marker.HousePlanIds.some((planId) => planId === id) ||
                (marker.HousingUnitId === id && marker.HousingUnitId)))
            .map((marker) => sitePlanMap?.querySelector(`#${marker.Id}`)?.cloneNode(true));

        updateLotPolygon(sitePlan.sitePlanId, LotPolygonType.Hovered.toString(), polygonLots);
    });
}

/**
 * handleInteractiveMarkerClick: this function handles the behavior of clicking on a interactive
 * marker rendered on top of a Interactive Site Plan.
 */
const handleInteractiveMarkerClick = ({ lotMarker, communityId, sitePlanId }) => {
    const state = store.getState();
    const currentPlans = selectAllPlansAndSpecsIds(state).plans;

    const availableSpec = lotMarker.HousingUnitId;
    const availablePlans = getLotAvailableListings(lotMarker.HousePlanIds, currentPlans);
    const currentLotCommunityId = selectLotCommunityId(state);
    const currentLotSpecId = selectAvailableSpec(state);
    const currentLotCardStatus = selectedLotSummaryCardStatus(state);

    const payload = {
        availablePlans,
        availableSpec,
        lotNumber: lotMarker.Label,
        lotId: lotMarker.Id,
        communityId,
        interactiveSitePlanCardStatus: currentLotCardStatus,
        lotStatus: lotMarker.Status,
        sitePlanId,
    };

    const isNewCommunityOrSpec =
        currentLotCommunityId !== communityId ||
        currentLotSpecId !== availableSpec;
    
    if (isNewCommunityOrSpec) {
        payload.interactiveSitePlanCardStatus = LoadingStatus.Loading.toString();
    }
      
    store.dispatch(actions.setLotInformation(payload));
}

/**
 * handleLayersVisibilityBasedOnZoomLevel: Adjusts the visibility of map layers on community interactive
 * site plans based on the zoom level.
 * Implements the "progressive disclosure" approach to dynamically enrich or simplify map details
 * as users zoom in or out.
 *
 * @param {number} zoomLevel - The current zoom level of the map.
 */
const handleLayersVisibilityBasedOnZoomLevel = async ({ zoomLevel }) => {
    const sitePlansNodes = document.querySelectorAll('[data-is-interactive-community="true"] [data-community-map-id]');
    const currentState = store.getState();
    const sitePlans = selectSitePlans(currentState);

    sitePlansNodes.forEach(async (sitePlanNode) =>  {
        const sitePlan = sitePlans.find((element) => element.sitePlanId === +sitePlanNode.dataset.sitePlanId);
        if (sitePlan) {
            await getInteractiveSitePlanLayers(zoomLevel, sitePlan.sitePlanLayers, sitePlanNode, sitePlan.communityId);
            handleInteractiveMapLayersVisibility(zoomLevel, sitePlanNode);
        }
    });
};

/**
 * goToInteractiveSitePlan: will place the map in the area where the community site plan
 * in interactive state will be shown.
 */
const goToInteractiveSitePlan = ({ latitude, longitude, shouldSwitchToHomeResults }) => {
    if (shouldSwitchToHomeResults) {
        dispatchEnterInteractiveSitePlanResults();
    }

    updateMapCenter({ latitude, longitude });
    setZoomLevel(zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger);
}

/**
 * activateInteractiveStateFromLotClicked will check if the current product type is homes,
 * if it is not homes, then this function will call the enableSitePlanInteraction in order
 * to switch to home results. This functionality is likely to happen when you were seeing an
 * ISP and then you switched to community results. Since you are still within the threshold
 * for engaging with ISPs, if you click on the actual ISP it will show again the lot polygons
 * and enable interaction.
 */
const activateInteractiveStateFromLotClicked = () => {
    if (selectProductType(store.getState()) !== ProductType.Homes.toString()) {
        enableSitePlanInteraction();
    }
}

/**
 * handleInteractiveSitePlanFetchResults will receive the fetch request for site plans
 * and dispatch them to the store so they can be use by the interested subscribers.
 * @param {Array} interactiveSitePlans of retrieved Interactive Site Plans.
 */
const handleInteractiveSitePlanFetchResults = (interactiveSitePlans) => {
    interactiveSitePlans
    .filter((sitePlan) => sitePlan.SitePlanLots.length > 0)
    .forEach((collection) => {
        trackLoadedSitePlans([collection.CommunityId], LoadingStatus.Succeeded.toString());

        const payload = {
            markers: collection.SitePlanLots,
            areMarkersShown: false,
            communityId: collection.CommunityId,
            sitePlanId: collection.SitePlanId,
        };
    
        store.dispatch(actions.setInteractiveSitePlan(payload));
    });
}

/**
 * dispatchSitePlanFormOpenEvent: Dispatches a custom event 'InteractiveMapBrochureCTA' on the document body.
 * This event is used to signal that a form was opened from the interactive site plan brochure CTA.
 * The event carries the provided listing information in its detail property for downstream consumers.
 *
 * @param {Object} listingInformation - The listing information to be sent with the event.
 */
const dispatchSitePlanFormOpenEvent = (listingInformation) => {
    document.body.dispatchEvent(
        new CustomEvent('interactiveMapBrochureCTA', {
            detail: {
                listingInformation
            },
        })
    );
}

/**
 * onProductTypeChanged$ will emit any change on the current product type.
 */
const onProductTypeChanged$ = store$.pipe(
    map(selectProductType),
    distinctUntilChanged());

/**
 * onProductTypeDistinctThanHomes$ will emit a value every time the product type changes while a site plan is shown
 * in interactive state. The change we target in this observable is any product type distinct than "Homes"
 */
const onProductTypeDistinctThanHomes$ = onProductTypeChanged$.pipe(
    filter((productType) => productType !== ProductType.Homes.toString()),
    withLatestFrom(onZoomChanged$),
    filter(([, zoomLevel]) => zoomLevel >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger));

/**
 * onProductTypeHomes$ will emit a value every time the product type changes while a site plan is shown
 * in interactive state. The change we target in this observable is product type to be "Homes"
 */
const onProductTypeHomes$ = onProductTypeChanged$.pipe(
    filter((productType) => productType === ProductType.Homes.toString()),
    withLatestFrom(onZoomChanged$),
    filter(([, zoomLevel]) => zoomLevel >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger));

/**
 * onSitePlansChanged$ will stream any change within the site plans from the store.
 */
const onSitePlansChanged$ = store$.pipe(
    map(selectSitePlans),
    distinctUntilChanged(),
    filter(Boolean));

/**
 * onNewCommunitiesWithInteractiveSitePlan$ will emit a value every time a new site plan
 * with interactive site plan is added in the store.
 */
const onNewCommunitiesWithInteractiveSitePlan$ = onSitePlansChanged$.pipe(
    map((sitePlans) => sitePlans.filter((sitePlan) => sitePlan.isInteractiveSitePlan)),
    shareReplay(1));

/**
 * onNewInteractiveSitePlans$ will emit values every time an interactive site plan is added or removed.
 */
const onNewInteractiveSitePlans$ = store$.pipe(
    map(selectInteractiveSitePlans),
    distinctUntilChanged());

/**
 * 
 */
const onTrackedInteractiveSitePlansChanged$ = store$.pipe(
    map(selectTrackedInteractiveSitePlans),
    distinctUntilChanged());

/**
 * onInteractiveSitePlanRemoved$ will stream every time a interactive site plan was removed from the store.
 */
const onInteractiveSitePlanRemoved$ = onSitePlansChanged$.pipe(
    pairwise(),
    map(([previous, current]) => {
        const previousInteractive = previous.filter(sitePlan => sitePlan.isInteractiveSitePlan);
        const currentInteractive =
            new Set(current.filter(sitePlan => sitePlan.isInteractiveSitePlan).map(sitePlan => sitePlan.sitePlanId));

        const removed = previousInteractive.filter(sitePlan => !currentInteractive.has(sitePlan.sitePlanId));
        return removed;
    }),
    filter(removed => removed.length > 0)
);

/**
 * onDistinctHomesMarkers$ emits the current plan and specs from the store.
 */
const onDistinctHomesMarkers$ = store$.pipe(
    filter((state) => selectResultType(state) === ResultType.Homes.toString()),
    map(selectAllPlansAndSpecsIds),
    distinctUntilChanged(),
    filter(({ plans }) => plans),
    filter(({ specs }) => specs),
    shareReplay(1));

/**
 * onFetchInteractiveSitePlans$ will emit a value every time interactive
 * markers should be fetched from the server in order to be drawn on top of a map.
 */
const onFetchInteractiveSitePlans$ = combineLatest([
        onNewCommunitiesWithInteractiveSitePlan$,
        onZoomChanged$]).pipe(
    filter(([, zoomLevel]) => zoomLevel >= zoomLevelTriggers.zoomLevelDrawSitePlansTrigger),
    filter(([sitePlans]) => sitePlans.length > 0),
    withLatestFrom(onTrackedInteractiveSitePlansChanged$),
    map(([[sitePlans], trackedSitePlans]) =>
        sitePlans.filter((sitePlan) => 
            !trackedSitePlans.some((trackedPlan) =>
                trackedPlan.communityId === sitePlan.communityId &&
                (trackedPlan.loadingStatus === LoadingStatus.Succeeded.toString() ||
                trackedPlan.loadingStatus === LoadingStatus.Loading.toString())
            )
        ).map((sitePlan) => sitePlan.communityId)),
    filter((communityIds) => communityIds.length > 0),
    tap((communityIds) => trackLoadedSitePlans(communityIds, LoadingStatus.Loading.toString())),
    mergeMap((communityIds) => getCommunityInteractiveSitePlanInformation$(communityIds)));

/**
 * onRemoveInteractiveMarkers$ emits a stream of site plans whose interactive markers 
 * should be removed when the user zooms out past a threshold where these markers are no longer needed. 
 * This ensures that markers are hidden when the zoom level falls below the required threshold.
 */
const onRemoveInteractiveMarkers$ = onZoomChanged$.pipe(
    pairwise(),
    filter(([previousZoom, currentZoom]) => 
        previousZoom === zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger &&
        currentZoom < zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger
    ),
    withLatestFrom(onProductTypeChanged$),
    filter(([, productType]) => productType === ProductType.Homes.toString()));
             
/**
 * onLotClicked$ emits a value when the area of an active lot is clicked.
 */
const onLotClicked$ = fromEvent(document.body, 'lotClicked').pipe(
    withLatestFrom(onNewInteractiveSitePlans$, onProductTypeChanged$),
    tap(activateInteractiveStateFromLotClicked),
    map(([{ detail }, allSitePlans, productType]) => {
        const activeLotId = detail.lotPath?.id;
        const interactiveSitePlans = getInteractiveSitePlans(Number(detail.communityId), allSitePlans);
        const sitePlan = interactiveSitePlans.find((plan) => 
            plan.markers.some((marker) => marker.Id === activeLotId)
        );
        const lotMarker = sitePlan?.markers.find((marker) => marker.Id === activeLotId);

        return {
            communityId: detail.communityId,
            lotMarker,
            sitePlanId: sitePlan?.sitePlanId,
            productType,
        };
    }),
    filter(({ lotMarker }) => Boolean(document.querySelector(`g#active #${lotMarker?.Id}`))),
    startWith({ communityId: null, lotMarker: null, sitePlanId: null, productType: null }),
    pairwise(),
    filter(([, currentState]) => currentState.productType === ProductType.Homes.toString()),
    tap(([previousState, currentState]) => updateActiveLotPolygonStyle(previousState, currentState)),
    map(([, currentState]) => currentState));
    
/**
 * interactiveMarkersReady$ will emit a value everytime a distinct
 * set of interactive markers is created.
 */
const interactiveMarkersReady$ = fromEvent(document.body, 'interactiveMarkersReady').pipe(
    map((event) => event.detail),
    filter(({ sitePlanId }) => !configuration.markersBuffer.has(sitePlanId)),
    mergeMap(({ sitePlanId }) => {
        configuration.markersBuffer.add(sitePlanId);
        return from(configuration.sitePlansBuffer).pipe(
            filter((id) => id === sitePlanId),
            map(() => sitePlanId)
        );
    }));

/**
 * sitePlanDrawComplete$ will emit a value everytime a distinct
 * site plan is drawn on top of the Search Results Map.
 */
const sitePlanDrawComplete$ = fromEvent(document.body, 'communityMapDrawComplete').pipe(
    map((event) => event.detail),
    filter(({ sitePlanId }) => !configuration.sitePlansBuffer.has(sitePlanId)),
    mergeMap(({ sitePlanId }) => {
        configuration.sitePlansBuffer.add(sitePlanId);
        return from(configuration.markersBuffer).pipe(
            filter((id) => id === sitePlanId),
            map(() => sitePlanId)
        );
    }));

/**
 * onMarkersAndInteractiveMapReady$ will emit a value everytime the combination of
 * interactive markers ready and site plan drawn events are emitted.
 */
const onMarkersAndInteractiveMapReady$ = merge(sitePlanDrawComplete$, interactiveMarkersReady$).pipe(
    tap(assignLotPathId),
    withLatestFrom(onDistinctHomesMarkers$, onProductTypeChanged$),
    filter(([, , productType]) => productType === ProductType.Homes.toString()),
    map(([, { plans, specs }]) => ({
        plans,
        specs,
    })),
    shareReplay(1));

/**
 * onHoveredCardChanged$ will stream a value every time a card enters or stops being hovered.
 */
const onHoveredCardChanged$ = store$.pipe(
    filter((state) => selectZoomLevel(state) >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger),
    filter((state) => selectProductType(state) === ProductType.Homes.toString()),
    map(selectFocusedListingAndSitePlans),
    distinctUntilChanged(),
    withLatestFrom(onNewInteractiveSitePlans$, onZoomChanged$),
    filter(([,, zoomLevel]) => zoomLevel >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger),
    tap(removeHighlightedElements),
    map(([{ focusedListing }, allSitePlans]) => ({
            id: Number(focusedListing?.id),
            interactiveSitePlans: getInteractiveSitePlans(Number(focusedListing?.communityId), allSitePlans),
            communityId: Number(focusedListing?.communityId),
    })));

/**
 * onHoveredLot$ will stream values every time the user is hovering on a lot polygon.
 */
const onHoveredLot$ = fromEvent(document.body, 'hoveredLot').pipe(
    map(({ detail }) => ({
        sitePlanId: detail.sitePlanId,
        groupName: LotPolygonType.Hovered.toString(),
        polygons: [detail.interestedLotPath?.cloneNode(true)],
        polygonId: detail.polygonId,
        polygonGroupId: detail.interestedLotPath?.closest('g')?.id,
    })),
    distinctUntilKeyChanged('polygonId'),
    tap(removeHighlightedElements),
    tap(handleCursorPointerOnHoveringLots),
    filter(({ polygonGroupId }) => polygonGroupId === LotPolygonType.Active.toString()),
    filter(({ polygonId }) => polygonId),
    withLatestFrom(store$),
    filter(([, state]) => selectZoomLevel(state) >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger),
    filter(([, state]) => selectProductType(state) === ProductType.Homes.toString()),
    filter(([, state]) => selectLayoutBreakpoint(state).text === LayoutBreakpoint.Big.text),
    map(([hoverInformation]) => hoverInformation));

/**
 * onInteractiveSitePlansRemoved$ will emit a value every time a community
 * interactive site plans that was in interactive state gets removed from the store.
 */
const onInteractiveSitePlansRemoved$ = onNewCommunitiesWithInteractiveSitePlan$.pipe(
    filter((sitePlans) => sitePlans.length === 0));

/**
 * onShowHiddenInteractiveMarkers$ will emit a value every time there are
 * Interactive Site Plans with markers not shown while in Homes product type.
 */
const onShowHiddenInteractiveMarkers$ = store$.pipe(
    filter((state) => selectZoomLevel(state) >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger),
    filter((state) => selectProductType(state) === ProductType.Homes.toString()),
    map(getHiddenInteractiveSitePlans),
    distinctUntilChanged((previous, current) => 
        previous.length === current.length && 
        previous.every((prevItem, index) => prevItem.sitePlanId === current[index].sitePlanId)
    ),
    filter((interactiveSitePlans) => interactiveSitePlans.length > 0)
);

/**
 * onExitInteractiveState$ is an observable that will emit a value anytime one
 * of its inner observables emit a value too. Once a value is emited, it
 * means the interactiveness of the site plan should end.
 */
const onExitInteractiveState$ = merge(
    onRemoveInteractiveMarkers$,
    onInteractiveSitePlansRemoved$,
    onProductTypeDistinctThanHomes$,
).pipe(
    withLatestFrom(store$),
    map(([, state]) => selectInteractiveSitePlans(state)),
    filter((sitePlans) => sitePlans.length > 0),
    tap(addNonInteractiveStateClass),
    debounceTime(100));

/** 
 * onInteractiveCommunitiesZoomChanged$ observable will emit values whenever
 * map zoom level changed and there are rendered community interactive markers on it.
 */
const onInteractiveCommunitiesZoomChanged$ = onZoomChanged$.pipe(
    withLatestFrom(onNewCommunitiesWithInteractiveSitePlan$),
    map(([zoomLevel, sitePlans]) => ({
        zoomLevel,
        sitePlans,
    })));

/**
 * onNewInteractiveSitePlanIds$ observable will emit values whenever the site plan ids change
 */
const onNewInteractiveSitePlanIds$ = onNewCommunitiesWithInteractiveSitePlan$.pipe(     
    map((interactiveSitePlans) => interactiveSitePlans.map(sitePlan => sitePlan.communityId)), 
     distinctUntilChanged((previous, current) => {    
        if (previous.length !== current.length) {
            return false;
        }         
        
        return previous.every((id, index) => id === current[index]); 
    }));

/**
 * onZoomEnteringInteractiveState$ observable will emit values whenever the map zoom level
 * changed and that zoom level matches the interactive state zoom level threshold.
 */
const onZoomEnteringInteractiveState$ = combineLatest([
    onZoomChanged$, 
    onNewInteractiveSitePlanIds$
]).pipe(
    distinctUntilChanged(([prevZoom, prevIds], [currZoom, currIds]) => {
        const isZoomUnchanged =
            currZoom >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger &&
            prevZoom >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger;
        const areIdsSame = prevIds.length === currIds.length && prevIds.every((id, index) => id === currIds[index]);

        return isZoomUnchanged && areIdsSame;
    }),
    pairwise(),
    filter(([[prevZoom], [currZoom]]) => currZoom > prevZoom),
    filter(([, [zoomLevel]]) => zoomLevel >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger),
    map(([, [, interactiveSitePlans]]) => interactiveSitePlans),
    filter((interactiveSitePlans) => interactiveSitePlans.length > 0));

/**
 * onGoToSitePlanButtonCliked$ will emit a value whenever the
 * "Interactive Map" button in a marker tooltip is clicked.
 */
const onGoToSitePlanButtonCliked$ = onDocumentClicked$.pipe(
    map(({ target }) =>
        target?.hasAttribute("data-interactive-plan-button")
            ? target
            : target?.closest('[data-tooltip-container]')?.querySelector('[data-interactive-plan-button]')
    ),
    filter(Boolean),
    withLatestFrom(onProductTypeChanged$),
    map(([goToSitePlanButton, productType]) => ({
            latitude: +goToSitePlanButton.dataset.latitude,
            longitude: +goToSitePlanButton.dataset.longitude,
            communityId: +goToSitePlanButton.dataset.communityId,
            shouldSwitchToHomeResults: productType !== ProductType.Homes.toString(),
    })),
);

/**
 * fetchInteractiveSitePlanFromTooltipClick$ will fetch the Interactive Site Plan associated
 * with the tooltip that received the click.
 */
const fetchInteractiveSitePlanFromTooltipClick$ = onGoToSitePlanButtonCliked$.pipe(
    withLatestFrom(onTrackedInteractiveSitePlansChanged$),
    filter(([{ communityId }, trackedSitePlans]) =>
        !trackedSitePlans.some((trackedPlan) =>
                trackedPlan.communityId === communityId &&
                (trackedPlan.loadingStatus === LoadingStatus.Succeeded.toString() ||
                trackedPlan.loadingStatus === LoadingStatus.Loading.toString())
        )
    ),
    map(([{ communityId }]) => communityId),
    tap((communityId) => trackLoadedSitePlans([communityId], LoadingStatus.Loading.toString())),
    switchMap((communityId) => getCommunityInteractiveSitePlanInformation$([communityId])));

/**
 * onLotInformationChanged$ will stream a value every time changes happen in the lot
 * information from the store.
 */
const onLotInformationChanged$ = store$.pipe(
    map(selectLotInformation),
    distinctUntilChanged(),
    skip(1));

/**
 * onLotInformationRemoved$ will stream a value every time lot information is removed from store.
 */
const onLotInformationRemoved$ = onLotInformationChanged$.pipe(
    pairwise(),
    filter(([previousLot]) => !!previousLot.lotId),
    filter(([, currentLot]) => !currentLot.lotId),
    map(([previousLot]) => ({
        sitePlanId: previousLot.sitePlanId,
        polygonId: previousLot.lotId,
    })));

/**
 * onEnterInteractiveState$ observable will stream values everytime the map should enter
 * interactive state for any of its Interactive Site Plans
 */
const onEnterInteractiveState$ = merge(
    onShowHiddenInteractiveMarkers$,
    onZoomEnteringInteractiveState$,
    onProductTypeHomes$,
    onMarkersAndInteractiveMapReady$).pipe(
        debounceTime(100)
    );

/**
 * handleInteractiveMarkersOnZoomChange$ will stream a value every time a zoom level changes.
 * The intention behind this is to show or hide the Interactive Site Plan markers.
 */
const handleInteractiveMarkersOnZoomChange$ = onZoomChanged$.pipe(
    withLatestFrom(store$),
    map(([zoomLevel, state]) => ({
        interactiveSitePlans: selectInteractiveSitePlans(state),
        zoomLevel
    })),
    filter(({ interactiveSitePlans }) => interactiveSitePlans.length > 0));

const onWheelScrollOverTooltip$ = fromEvent(document, 'wheel', { passive: false }).pipe(
    filter((event) => event.target.closest('[data-tooltip-container]')),
    tap((event) => event.preventDefault()));

/**
 * onSitePlanFormOpen$ emits the listing information when a site plan form modal is opened
 * and the corresponding interactive site plan is currently shown (markers are visible).
 */
const onSitePlanFormOpen$ = store$.pipe(
  map(selectModal),
  distinctUntilChanged(),
  filter(Boolean),
  filter((modal) => modal?.name === "leadGenerator"),
  withLatestFrom(onNewInteractiveSitePlans$),
  filter(([modal, interactiveSitePlans]) => {
    const listingInformation = modal?.listingInformation;
    return interactiveSitePlans.find((sitePlan) =>
      sitePlan.areMarkersShown &&
      Number(sitePlan.communityId) === Number(listingInformation?.communityId)
    );
  }),
  map(([modal]) => modal.listingInformation),
  filter(Boolean)
);

onDistinctHomesMarkers$.subscribe(updatePlansVisibility);
onDistinctHomesMarkers$.subscribe(updateSpecsVisibility);
onLotClicked$.subscribe(handleInteractiveMarkerClick);
onHoveredCardChanged$.subscribe(highlightLotArea);
onInteractiveCommunitiesZoomChanged$.subscribe(handleLayersVisibilityBasedOnZoomLevel);
onGoToSitePlanButtonCliked$.subscribe(goToInteractiveSitePlan);
onInteractiveSitePlanRemoved$.subscribe(handleRemovedInteractiveSitePlans);
onExitInteractiveState$.subscribe(disableSitePlanInteraction);
onEnterInteractiveState$.subscribe(enableSitePlanInteraction);
handleInteractiveMarkersOnZoomChange$.subscribe(handleMarkersVisibility);
onWheelScrollOverTooltip$.subscribe();
fetchInteractiveSitePlanFromTooltipClick$.subscribe(handleInteractiveSitePlanFetchResults);
onFetchInteractiveSitePlans$.subscribe(handleInteractiveSitePlanFetchResults);
onSitePlanFormOpen$.subscribe(dispatchSitePlanFormOpenEvent);
onLotInformationRemoved$.subscribe((lotInformation) =>
    clearSitePlanGroup(lotInformation.sitePlanId, LotPolygonType.Selected.toString()));
onHoveredLot$.subscribe((hoveredLotInformation) =>
    updateLotPolygon(
        hoveredLotInformation.sitePlanId,
        hoveredLotInformation.groupName,
        hoveredLotInformation.polygons));